import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";

import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { MobileStickyCta } from "@/components/marketing/MobileStickyCta";
import { Section } from "@/components/marketing/layout/Section";
import { Card, CardContent } from "@/components/ui/card";
import { AnswerCard } from "@/components/marketing/solution/AnswerCard";
import { ProblemCards } from "@/components/marketing/solution/ProblemCards";
import { WorkflowShowcase } from "@/components/marketing/solution/WorkflowShowcase";
import { SolutionCta } from "@/components/marketing/solution/SolutionCta";
import {
  CONTACT_SOURCE_TAGS,
  PRIMARY_CTA_LABEL,
  QUALIFIER_PATH,
  STRAPLINE,
} from "@/components/marketing/solution/constants";
import {
  FRACTIONAL_FROM,
  formatRand,
} from "@/components/marketing/solution/pricing";
import { enquiryMailto } from "@/lib/enquiry";
import { ENQUIRY_EMAIL, GOOGLE_REVIEW_URL } from "@/lib/brand";

export const Route = createFileRoute("/")({
  component: NewHomePage,
});

const SPINE = [
  {
    step: "Connect",
    title: "Every system, one place",
    copy: "Stock, jobs, sales, POS, payroll, accounting - cloud or on-premise - synced securely into your own private database.",
  },
  {
    step: "Understand",
    title: "Ask, report, trust",
    copy: "Live dashboards, operational reports, and plain-English questions with cited answers. One version of the truth.",
  },
  {
    step: "Act",
    title: "Automate the in-between",
    copy: "Workflows watch your data and act - reorders, statements, follow-ups, invoices - without anyone remembering to check.",
  },
];

/**
 * Real Google Business Profile reviews, screenshotted and supplied by Allan
 * 2026-08-30 (5.0★ aggregate from 7 reviews at the time). Trimmed for length
 * where noted; wording otherwise verbatim. Reviewer names shortened to first
 * name + surname initial - real, verifiable reviews, just not full names on
 * the public page.
 */
const GOOGLE_REVIEWS = [
  {
    id: "google-jared",
    eyebrow: "Automation, built to fit",
    body: "Great automation company that takes the time to understand your requirements and help resolve them in the most cost efficient and time efficient way possible.",
    who: "Jared D., Google review",
  },
  {
    id: "google-rika",
    eyebrow: "Divisional reporting for the board",
    // Trimmed from a longer review; wording of the retained sentence is verbatim.
    body: "We partnered with Larasoft to help us develop our divisional financial reporting for the Board in Power BI, in the aim to automate the data flow from Xero.",
    who: "Rika V., Google review",
  },
  {
    id: "google-sheetal",
    eyebrow: "Reporting, implemented end-to-end",
    // Trimmed from a longer review (dropped a middle sentence); wording of
    // the retained sentences is verbatim.
    body: "Allan and his team at Larasoft have helped us immensely with the reporting requirements we needed. From understanding to implementation and roll-out, it was a smooth process.",
    who: "Sheetal B., Google review",
  },
] as const;

/**
 * Only claims we can stand behind are repeated here.
 *
 * "99% sync success rate" and "50k+ records per hour" were dropped (Allan,
 * 2026-08-01): both are throughput numbers a prospect can challenge and neither
 * is the reason anyone buys. The replacements are promises rather than
 * measurements, which is why they need no sourcing - "no per-user fees" is our
 * own pricing policy.
 *
 * "Your own private database" was dropped (Allan, 2026-09-02): it is a point
 * better made in the sales conversation than asserted as a headline stat.
 *
 * The source mockup also asserted "10+ countries" and "building since 2019";
 * both are unverified, so they stay out pending Allan's confirmation.
 */
const STATS = [
  { value: "20+", label: "prebuilt connectors" },
  { value: "24/7", label: "monitored syncs" },
  { value: "Zero", label: "per-user fees" },
];

const JOURNEY = [
  {
    badge: "Step 1 · Call",
    title: "Tell us what has slipped",
    copy: "A short call about your systems and the one problem that costs you the most. If we can see the win, we'll agree whether to start with a tightly scoped pilot or go straight to a full rollout.",
    meta: "± 30 minutes · no obligation",
  },
  {
    badge: "Step 2 · Pilot",
    title: "We prove it on your data",
    copy: "We connect a slice of your real systems and build a small, tightly scoped pilot - your own data answering your own question.",
    meta: "small scope · fixed timeline",
  },
  {
    badge: "Step 3 · Deploy",
    title: "Go live and grow",
    copy: "The pilot becomes production: monitored syncs, dashboards and workflows, expanding as your organisation needs it.",
    meta: "your whole team included",
  },
];

function NewHomePage() {
  return (
    <MarketingLayout>
      <Helmet>
        <title>Larasoft - Ask your data anything</title>
        <link rel="canonical" href="https://larasoft.global/" />
        <meta
          name="description"
          content="More systems, more sites, more people - and no single view of what's happening. Larasoft connects your systems into one trusted database, then answers questions and automates the work in between."
        />
      </Helmet>

      {/* Hero */}
      <Section className="py-14 sm:py-20">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <div className="space-y-6">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-blue-medium">
              When your business knows more than any one person ever could
            </p>
            <h1 className="text-4xl font-extrabold leading-[1.1] text-brand-blue-dark sm:text-5xl lg:text-6xl">
              Ask your data{" "}
              <span className="text-brand-blue-medium">anything.</span>
            </h1>
            <p className="max-w-xl text-lg text-slate-600">
              More systems, more people, no single view of what's happening.
              We connect it all into one trusted database, then bring the AI,
              the automation, and a team to run it - alongside yours.
            </p>
            <div className="flex flex-wrap gap-3">
              <SolutionCta
                event="hero_primary_cta"
                eventDetail="home"
                search={{ source: CONTACT_SOURCE_TAGS.home }}
              >
                {PRIMARY_CTA_LABEL}
              </SolutionCta>
              <SolutionCta
                to={QUALIFIER_PATH}
                event="hero_secondary_cta"
                eventDetail="home"
                variant="outline"
              >
                Start with a pilot - or go straight in
              </SolutionCta>
            </div>
            <p className="font-mono text-xs text-slate-500">
              A call, then a pilot on your data or straight to deploy - you
              decide.
            </p>
          </div>
          <AnswerCard />
        </div>
      </Section>

      {/*
        Sits immediately after the hero: MobileStickyCta reveals itself once its
        sentinel scrolls out of view, so anywhere further down the page it would
        only appear at the very bottom - overlapping the final CTA it duplicates.
        Matches the placement on the live homepage.
      */}
      <MobileStickyCta />

      {/* Connect · Understand · Act */}
      <Section
        containerClassName="space-y-8"
        className="border-y border-slate-200 bg-slate-50 py-14"
      >
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-blue-medium">
            How it works
          </p>
          <h2 className="text-2xl font-bold text-brand-blue-dark sm:text-3xl">
            Connect. Understand. Act.
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {SPINE.map((item) => (
            <Card key={item.step}>
              <CardContent className="space-y-3 p-7">
                <span className="inline-block rounded-full bg-brand-blue-dark px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-white">
                  {item.step}
                </span>
                <h3 className="text-lg font-bold text-brand-blue-dark">
                  {item.title}
                </h3>
                <p className="text-slate-600">{item.copy}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* Problems */}
      <Section id="problems" containerClassName="space-y-10" className="py-16 sm:py-20">
        <div className="max-w-3xl space-y-3">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-blue-medium">
            Start with your problem
          </p>
          <h2 className="text-3xl font-extrabold text-brand-blue-dark sm:text-4xl">
            What does your business already know that nobody can see?
          </h2>
          <p className="text-lg text-slate-600">
            The answers are in there. They are just spread across systems that
            do not talk to each other, and nobody has the hours to go and find
            them. If one of these sounds like your Tuesday, that is where we
            start.
          </p>
        </div>
        <ProblemCards />
      </Section>

      {/* Workflow automation */}
      <Section
        id="workflow"
        containerClassName="space-y-8"
        className="bg-brand-navy py-16 sm:py-20"
      >
        <div className="max-w-3xl space-y-3">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-turquoise">
            Workflow automation
          </p>
          {/*
            The workflow builder is NOT released to clients - we configure
            workflows for them. Copy here must never imply self-service: no
            "you build it", no "switch it on yourself". Framed as done-for-you,
            which is the honest version and the stronger promise anyway.
          */}
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            Describe the workflow. We build it for you.
          </h2>
          <p className="text-lg text-slate-400">
            Automations for your operation, not just your books. Tell us what
            should happen in plain English - we build it, dry-run it against your
            own data, and switch it on once you are happy. Nobody on your team
            has to learn a builder.
          </p>
        </div>
        <WorkflowShowcase />
        <p className="text-sm text-slate-400">
          Want it run for you end-to-end, not just switched on? That's our
          fractional AI &amp; automation team, working inside your business
          from {formatRand(FRACTIONAL_FROM)} a month.
        </p>
      </Section>

      {/* Proof */}
      <Section containerClassName="space-y-10" className="py-16 sm:py-20">
        <div className="max-w-3xl space-y-3">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-blue-medium">
            Proof
          </p>
          <h2 className="text-3xl font-extrabold text-brand-blue-dark sm:text-4xl">
            Trusted by teams that depend on accurate data
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {GOOGLE_REVIEWS.map((review) => (
            <Card key={review.id} className="bg-slate-50">
              <CardContent className="space-y-3 p-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-brand-blue-medium">
                  {review.eyebrow}
                </p>
                <blockquote className="text-sm text-slate-700">
                  “{review.body}”
                </blockquote>
                <a
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block font-mono text-xs text-slate-500 hover:underline"
                >
                  {review.who}
                </a>
              </CardContent>
            </Card>
          ))}
        </div>

        {/*
          Offered instead of padding the section with more quotes. We show only
          the real Google reviews above and will not compose others; a real
          conversation with a real customer is worth more than invented
          testimonials, and it is a promise we can actually keep.
        */}
        <p className="max-w-2xl text-slate-600">
          Would you rather hear it from a customer than from us? During the
          pilot we can arrange a conversation with one of ours.
        </p>

        <dl className="flex flex-wrap gap-x-12 gap-y-6">
          {STATS.map((stat) => (
            <div key={stat.label}>
              <dt className="sr-only">{stat.label}</dt>
              <dd>
                <span className="block text-3xl font-extrabold text-brand-blue-dark">
                  {stat.value}
                </span>
                <span className="font-mono text-xs uppercase tracking-wider text-slate-500">
                  {stat.label}
                </span>
              </dd>
            </div>
          ))}
        </dl>
      </Section>

      {/* How we work */}
      <Section
        id="journey"
        containerClassName="space-y-10"
        className="border-t border-slate-200 bg-slate-50 py-16 sm:py-20"
      >
        <div className="max-w-3xl space-y-3">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-blue-medium">
            How we work
          </p>
          <h2 className="text-3xl font-extrabold text-brand-blue-dark sm:text-4xl">
            A call. Then a pilot - or straight in.
          </h2>
          <p className="text-lg text-slate-600">
            No demos of someone else's data. Start with a tightly scoped
            pilot on yours, or go straight to a full rollout if you're
            ready - your call. We take it on where we can see a clear win
            for both sides: a genuine problem we know we can solve, for a
            business ready to act on the answer.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {JOURNEY.map((item) => (
            <Card key={item.badge} className="relative">
              <CardContent className="space-y-3 p-7 pt-8">
                <span className="absolute -top-3 left-7 rounded-full bg-brand-blue-medium px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-white">
                  {item.badge}
                </span>
                <h3 className="text-lg font-bold text-brand-blue-dark">
                  {item.title}
                </h3>
                <p className="text-slate-600">{item.copy}</p>
                <p className="font-mono text-xs text-slate-500">{item.meta}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* Final CTA */}
      <Section
        id="book"
        containerClassName="space-y-6 text-center"
        className="bg-gradient-to-r from-brand-blue-dark via-brand-blue-medium to-brand-blue-dark py-20 text-white"
      >
        <h2 className="text-3xl font-extrabold sm:text-4xl">
          Let your business tell you what it knows.
        </h2>
        {/* The strapline as the closing beat, at heading weight - the whole
            page argues towards it. */}
        <p className="text-xl font-extrabold text-brand-turquoise sm:text-2xl">
          {STRAPLINE}.
        </p>
        <p className="mx-auto max-w-xl text-lg text-white/85">
          Start with a call about the one thing that has slipped. We'll prove
          the answer with a tightly scoped pilot on your own data, or go
          straight to a full rollout if you're ready - then run it
          yourselves, or with our team alongside you.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <SolutionCta
            event="final_cta"
            eventDetail="home"
            search={{ source: CONTACT_SOURCE_TAGS.home }}
            className="bg-white text-brand-blue-dark hover:bg-white/90"
          >
            {PRIMARY_CTA_LABEL}
          </SolutionCta>
        </div>
        <p className="font-mono text-sm text-white/70">
          or email{" "}
          <a href={enquiryMailto("/")} className="text-white underline">
            {ENQUIRY_EMAIL}
          </a>
        </p>
      </Section>

    </MarketingLayout>
  );
}
